import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Alert, Box, Button, CircularProgress, colors, DialogActions, Divider, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import {
  bulkUpdateConsumableScopedRates,
  BulkUpdateDefaultRatesResponse,
  ImportMode,
} from 'api/consumablesApi';
import { Branch } from 'api/branchApi';
import { Client } from 'api/clientsApi';
import { handleMutationError } from 'util/handleMutationError';
import { isComboReady, RateKind } from '../Utils/Utils';
import BulkUpdateDefaultRatesResultView from './BulkUpdateDefaultRatesResultView';
import { comboParts, comboLabel } from '../Utils/Utils';

export { comboParts, comboLabel };

export interface TabProps {
  mode: ImportMode;
  onBusyChange: (busy: boolean) => void;
  onClose: () => void;
}

export const ComboHeading = ({
  branch,
  client,
  verb,
}: {
  branch: Branch | null;
  client: Client | null;
  verb: string;
}) => (
  <Typography variant="h6" sx={{ m: 2, mb: 0 }}>
    {verb}{' '}
    {comboParts(branch, client).map((p, i, arr) => (
      <React.Fragment key={p.label}>
        {p.label} -{' '}
        <Typography component="span" variant="h6" sx={{ fontWeight: 'bold' }}>
          {p.value}
        </Typography>
        {i < arr.length - 1 ? ' and ' : ''}
      </React.Fragment>
    ))}
  </Typography>
);

// Reuses the previewed plan
export const useScopedRateTab = (rateKind: RateKind) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [file, setFile] = useState<File | null>(null);
  const [branch, setBranch] = useState<Branch | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [preview, setPreview] = useState<BulkUpdateDefaultRatesResponse | null>(
    null
  );
  const [result, setResult] = useState<BulkUpdateDefaultRatesResponse | null>(
    null
  );

  const { mutate: commitMutate, isLoading: isCommitLoading } = useMutation(
    bulkUpdateConsumableScopedRates,
    {
      onSuccess: async (data) => {
        await queryClient.invalidateQueries(['consumables-items']);
        setResult(data);
        enqueueSnackbar(
          data.errorCount === 0
            ? `Rates updated for ${comboLabel(branch, client)}`
            : `Rates updated for ${comboLabel(branch, client)} — ${data.errorCount} row(s) skipped`,
          { variant: 'success' }
        );
      },
      onError: (error) => {
        enqueueSnackbar(
          `Failed to update rates: ${handleMutationError(error)}`,
          { variant: 'error' }
        );
      },
    }
  );

  const comboReady = isComboReady(rateKind, branch, client);

  return {
    file,
    setFile,
    branch,
    setBranch,
    client,
    setClient,
    preview,
    setPreview,
    result,
    setResult,
    commitMutate,
    isCommitLoading,
    comboReady,
  };
};

export const ScopedRateResultSteps = ({
  preview,
  result,
  isCommitLoading,
  comboLabel: label,
  mode,
  branch,
  client,
  onBack,
  onConfirm,
  onClose,
}: {
  preview: BulkUpdateDefaultRatesResponse;
  result: BulkUpdateDefaultRatesResponse | null;
  isCommitLoading: boolean;
  comboLabel: string;
  mode: ImportMode;
  branch: Branch | null;
  client: Client | null;
  onBack: () => void;
  onConfirm: () => void;
  onClose: () => void;
}) => {
  const resultDivergedFromPreview =
    !!result &&
    (preview.createdCount !== result.createdCount ||
      preview.updatedCount !== result.updatedCount ||
      preview.ignoredCount !== result.ignoredCount ||
      preview.errorCount !== result.errorCount);

  if (result) {
    return (
      <>
        <ComboHeading
          branch={branch}
          client={client}
          verb={mode === 'update' ? 'Updated existing rates for' : 'Imported new rates for'}
        />
        <BulkUpdateDefaultRatesResultView response={result} />
        {resultDivergedFromPreview && (
          <Alert severity="warning" sx={{ m: 2, mt: 0 }}>
            The committed result differs from the preview you approved,
            data may have changed, or effective dates rolled over since
            preview. Review the details below.
          </Alert>
        )}
        <DialogActions>
          <Box mr={3} mt={1} mb={2}>
            <Button
              variant="contained"
              color="primary"
              endIcon={<CheckIcon />}
              onClick={onClose}>
              Done
            </Button>
          </Box>
        </DialogActions>
      </>
    );
  }

  return (
    <>
      <ComboHeading
        branch={branch}
        client={client}
        verb={mode === 'update' ? 'Updating existing rates for' : 'Importing new rates for'}
      />
      <BulkUpdateDefaultRatesResultView response={preview} />
      <Divider />
      <DialogActions>
        <Box mr={3} mt={1} mb={2}>
          <Button
            onClick={onBack}
            sx={{ color: colors.grey[600], mr: 1 }}
            disabled={isCommitLoading}>
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={
              isCommitLoading ||
              !preview.previewToken ||
              (preview.createdCount === 0 && preview.updatedCount === 0)
            }
            endIcon={
              isCommitLoading ? <CircularProgress size={15} /> : <CheckIcon />
            }
            onClick={onConfirm}>
            {isCommitLoading
              ? 'Please wait...'
              : preview.errorCount > 0
                ? `Ignore Errors and Confirm Import for ${label}`
                : `Confirm Import for ${label}`}
          </Button>
        </Box>
      </DialogActions>
    </>
  );
};
